/**
 * @module components/DimensionPercentage
 * @description 表格「指定维度合计占比」配置（ChartSettingsV2）
 *
 * 位置：高级 → 列总计占比 下方
 * 默认态：收起
 *
 * 交互模式（与后端契约 queries.customizeSetting.table.dimension_percentage 同构）— 更新于 2026-06-30：
 *   每条 metric 卡片包含（自上而下）：
 *     - 指标（tea-component Select 单选下拉；value = `${table_code}@${metric}`，无 × 删除按钮）
 *     - 聚合维度 group_by（TagFilterSelect 多选，影响行内合计占比分母）
 *     - 标题名称 label（Input 输入框；超长自动截断 100 字；支持任意特殊字符；以 `%` 开头合法）
 *   - 卡片整体淡灰色背景（#f7f8fa + 4px 圆角 + 12px 卡片间距），三行 + 标题整体包在一张大灰底里
 *   - 列表底部 +添加 / +Add 按钮
 *   - 本模块不再做"标题名称为空/聚合维度为空"的红字实时校验，
 *     统一交给后端 / sanitizeDimensionPercentage 在提交 / 联动阶段清洗
 *   - label 不允许为空：
 *     - 默认形态 = `%${metricDisplay}`（例：「%付费用户数」/ 「%PayingUsers」），由 MetricForm.handleMetricChange 自动填入
 *     - UI 失焦（handleLabelBlur）时若 labelDraft 为空 → 立即回退为默认
 *     - 数据层兜底（sanitizeDimensionPercentage）若 label 为空 + 能查到 metric 展示名 → 重置为默认
 *
 * 状态管理模式：
 *   读/写: useSettingObjectState('dimension_percentage')
 *
 * 展示模式与 PercentageOfColumnTotal / 旧版 PercentageBySelectedDimension 保持一致：
 *   - 标题名称 label 灰色 #9b9fb0、80px 宽、右间距 20px、文字左对齐、不换行
 *   - 控件（select / input）宽度 220px
 *
 * 新增指标时 label 默认空；用户选择指标后由 MetricForm.handleMetricChange 自动填入
 * 「%{metricDisplay}」（例：%付费用户数 / %PayingUsers）。
 * 切换指标时若旧 label 等于旧指标的默认 label 形式则同步刷新；用户自定义过的 label 保留。
 *
 * 后端契约要点：
 *   - group_by 在每条 metrics[] 内（不在根级）
 *   - metrics[].group_by 必填，≥1 个
 *   - group_by.column 必须在 queries.group_by 内
 *   - metrics[].metric 必须在 queries.metrics 内
 *   - metrics[].label trim 后非空，最长 100 字（label 可以以 `%` 开头，不受「metric/group_by.column 不得以 % 开头」的限制影响）
 *   - metric / group_by.column 不得以 `%` 或 `dim_pct_` 开头
 *   - column_sort.column = `dim_pct_{metric}`（多条配置按 metrics[] 顺序追加 `_2` / `_3`）
 *
 * 联动：
 *   - column_sort 自动注入 `dim_pct_{metric}` 虚拟列（按源指标子序列顺序插入）
 *   - QB prune：metric/dim 被删 → 从对应项裁剪；裁剪后任一不合法则整条剔除
 *   - QB migrate：metric/dim 列名改名 → group_by.column / metrics.metric 引用同步
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { Input, Select } from 'tea-component';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import TagFilterSelect from '@/pages/datalab/components/TagFilterSelect';
import PlusButton from '@/pages/datalab/components/PlusButton';
import IconButtonWithBubble from '@/pages/datalab/components/IconButtonWithBubble';
import { getLanguage } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import { DATALAB_SPLIT_STR } from '@dbl/core/global';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { useSettingObjectState } from '../../context';
import { useQueryColumnDimensions, useQueryColumnMetrics } from '../../hooks/deps';
import {
  createDefaultDimensionPercentageConfig,
  createDefaultDimensionPercentageMetric,
  defaultLabelForMetric,
  DIMENSION_PERCENTAGE_LABEL_MAX_LENGTH,
  hasForbiddenPrefix,
  isDimensionPercentageGroupByValid,
  type IDimensionPercentageConfig,
  type IDimensionPercentageGroupByItem,
  type IDimensionPercentageMetricItem,
} from '../../types/settings/dimensionPercentage';
import './index.scss';

interface IMetricOption {
  text: string;
  value: string;
}

interface IDimensionOption {
  text: string;
  value: string;
}

interface IMetricFormProps {
  count: number;
  /**
   * 其他卡片的「已选 metric」信息（不含本卡片）。
   * 用于在 handleMetricChange / handleLabelBlur 内部实时算「同 metric 在其他卡片中出现的次数」，
   * 给默认 label 附加 `_2` / `_3` 后缀——与 column 命名 `dim_pct_{metric}_{n}` 同构，
   * **直接写入** data.label：UI Input 显示、表格里 display_cn、记录里的 label 三者统一。
   *
   * 为什么是「其他卡片列表」而不是一个 number？
   * - 空卡片（未选 metric）选 metric 那一瞬间，本卡片 metric 仍是空，prop-based sameMetricCount
   *   永远是 0（命中 `if (!m?.metric) return 0`）→ 拿不到正确后缀
   * - 改为传 otherMetrics，让 MetricForm 在 onChange 内部基于"其他卡片的已选 metric"算 sameMetricCount
   */
  otherMetrics: Array<{ table_code: string; metric: string }>;
  data: IDimensionPercentageMetricItem;
  metricOptions: IMetricOption[];
  dimensionOptions: IDimensionOption[];
  onChange: (data: IDimensionPercentageMetricItem) => void;
  onDelete: () => void;
}

/** 给默认 label 附加「同 metric 重复」后缀（不依赖 state，pure function） */
function withSameMetricLabelSuffix(base: string, sameMetricCount: number): string {
  if (!base) return base;
  if (!sameMetricCount || sameMetricCount < 1) return base;
  return `${base}_${sameMetricCount + 1}`;
}

/** 单条 metric 配置：metric 单选 + group_by 多选 + label 输入 + 删除 */
const MetricForm: React.FC<IMetricFormProps> = ({
  count,
  otherMetrics,
  data,
  metricOptions,
  dimensionOptions,
  onChange,
  onDelete,
}) => {
  const { formatMessage, locale } = useIntl();
  const [labelDraft, setLabelDraft] = useState<string>(data?.label || '');

  // 基于「其他卡片的已选 metric」实时算 sameMetricCount
  // 例：otherMetrics=[{t0, revenue}]，本卡片选 revenue → sameMetricCount=1 → 默认 label 加 _2
  // 注意：thisKey 必须含 table_code，避免跨表同 metric 串扰
  const computeSameMetricCount = useCallback(
    (tableCode: string, metric: string): number => {
      if (!metric) return 0;
      const tc = tableCode || 't0';
      const targetKey = `${tc}::${metric}`;
      return otherMetrics.filter(
        o => `${o?.table_code || 't0'}::${o?.metric}` === targetKey,
      ).length;
    },
    [otherMetrics],
  );

  // 切换指标后：若 label 仍为旧指标的默认 label（或空），同步刷新为新指标名
  // 否则保留用户自定义
  useEffect(() => {
    setLabelDraft(data?.label || '');
  }, [data?.label, data?.metric]);

  // 已选中的 metric 在 metricOptions 中找不到（被删除 / 列名变更 / 切换表等场景）
  // → 一次性清空整张卡片的所有用户输入（metric / table_code / label / group_by），
  //   整张卡片保留供用户重新选择；保持"整张卡片回到未填写态"的一致性
  // 不会形成循环：清空后 data.metric === ''，下一轮 effect 直接 return
  useEffect(() => {
    if (!data?.metric) return;
    if (metricOptions.length === 0) return; // options 尚未就绪，等下一轮 metricOptions 更新后再判定
    const code = data?.table_code || 't0';
    const combined = `${code}${DATALAB_SPLIT_STR}${data.metric}`;
    const exists = metricOptions.some(o => o?.value === combined);
    if (exists) return;
    setLabelDraft('');
    onChange({
      table_code: 't0',
      metric: '',
      label: '',
      group_by: [],
    });
  }, [data?.metric, data?.table_code, metricOptions, onChange]);

  const handleMetricChange = useCallback(
    (value: string | undefined) => {
      const first = value || '';
      const [code = 't0', name = ''] = first.split(DATALAB_SPLIT_STR);
      const display = metricDisplayOf(metricOptions, first);
      const next: IDimensionPercentageMetricItem = {
        ...data,
        metric: name,
        table_code: code,
      };
      // 旧 label 等于旧指标的默认 label 形式（空 或 `%{oldMetricName}`） → 同步刷新为新指标
      const oldDisplay = data?.metric
        ? metricDisplayOf(metricOptions, `${data?.table_code || 't0'}${DATALAB_SPLIT_STR}${data.metric}`)
        : '';
      const oldDefault = oldDisplay ? defaultLabelForMetric(oldDisplay) : '';
      const isOldDefaultOrEmpty = !data?.label || data?.label === oldDefault;
      if (isOldDefaultOrEmpty) {
        // 实时算「其他卡片同 metric 数量」（空卡片选 metric 时也能正确拿到 sameMetricCount）
        const sameMetricCount = computeSameMetricCount(code, name);
        const newLabel = withSameMetricLabelSuffix(
          defaultLabelForMetric(display),
          sameMetricCount,
        );
        next.label = newLabel;
        setLabelDraft(newLabel);
      }
      onChange(next);
    },
    [data, metricOptions, onChange, computeSameMetricCount],
  );

  const handleLabelChange = useCallback(
    (value: string) => {
      const truncated =
        value.length > DIMENSION_PERCENTAGE_LABEL_MAX_LENGTH
          ? value.slice(0, DIMENSION_PERCENTAGE_LABEL_MAX_LENGTH)
          : value;
      setLabelDraft(truncated);
      onChange({ ...data, label: truncated });
    },
    [data, onChange],
  );

  // 失焦：label 不允许为空；清空后直接失焦（或保存）→ 自动回退到 `%${metricDisplay}`
  // 同 metric 重复场景下，回退时也按 sameMetricCount 附加 _2/_3 后缀（与 handleMetricChange 保持一致）
  const handleLabelBlur = useCallback(() => {
    if (labelDraft && labelDraft.length > 0) return; // 用户已输入内容，保持原值
    if (!data?.metric) return; // 还没有选择 metric，无可计算的默认 label
    const combined = `${data?.table_code || 't0'}${DATALAB_SPLIT_STR}${data.metric}`;
    const display = metricDisplayOf(metricOptions, combined);
    if (!display) return; // 找不到 metric 展示名，保留空值不动（sanitize 阶段再兜底）
    // 实时算「其他卡片同 metric 数量」（本卡片 metric 已选，从 otherMetrics 算其他卡片的同 metric 数量）
    const sameMetricCount = computeSameMetricCount(
      data.table_code || 't0',
      data.metric,
    );
    const newLabel = withSameMetricLabelSuffix(
      defaultLabelForMetric(display),
      sameMetricCount,
    );
    if (newLabel === labelDraft) return;
    setLabelDraft(newLabel);
    onChange({ ...data, label: newLabel });
  }, [data, labelDraft, metricOptions, onChange, computeSameMetricCount]);

  const handleGroupByChange = useCallback(
    (selected: string[]) => {
      const nextGroupBy: IDimensionPercentageGroupByItem[] = selected
        .filter(Boolean)
        .map(col => {
          const old = (data?.group_by || []).find(g => g?.column === col);
          return (
            old || {
              table_code: 't0',
              column: col,
            }
          );
        });
      onChange({ ...data, group_by: nextGroupBy });
    },
    [data, onChange],
  );

  const metricValue = useMemo(() => {
    if (!data?.metric) return undefined;
    const code = data?.table_code || 't0';
    return `${code}${DATALAB_SPLIT_STR}${data.metric}`;
  }, [data?.metric, data?.table_code]);

  const groupByValue = useMemo(
    () =>
      (data?.group_by || [])
        .map(g => g?.column)
        .filter(Boolean) as string[],
    [data?.group_by],
  );

  return (
    <div className="dbl-dimension-percentage-form-card">
      <div className="dbl-dimension-percentage-form-card-header">
        <span className="dbl-dimension-percentage-form-card-title">
          {formatMessage(
            { id: 'dbl_chart_dimension_percentage_metric_index' },
            { count },
          )}
        </span>
        <IconButtonWithBubble
          bubbleConfig={{
            content: formatMessage({ id: 'dbl_common_delete' }),
            placement: 'top-end',
            arrowPointAtCenter: true,
            placementOffset: 15,
          }}
          iconConfig={{
            onClick: onDelete,
            type: 'close',
          }}
        />
      </div>

      <div className="dbl-dimension-percentage-row">
        <span className="dbl-dimension-percentage-label">
          {formatMessage({ id: 'dbl_chart_metric' })}
        </span>
        <Select
          className="dbl-dimension-percentage-select"
          appearance="button"
          value={metricValue}
          options={metricOptions}
          listWidth={220}
          listHeight={180}
          placeholder={formatMessage({
            id: 'dbl_chart_dimension_percentage_metric_placeholder',
          })}
          tips={
            <div className="dbl-dimension-percentage-select-tips">
              <span>
                {formatMessage({
                  id: 'dbl_chart_dimension_percentage_metric_dropdown_title',
                })}
              </span>
            </div>
          }
          onChange={handleMetricChange}
        />
      </div>

      <div className="dbl-dimension-percentage-row">
        <span className="dbl-dimension-percentage-label">
          {formatMessage({ id: 'dbl_chart_group_by' })}
        </span>
        <TagFilterSelect
          className="dbl-dimension-percentage-select"
          value={groupByValue}
          name={formatMessage({
            id: 'dbl_chart_dimension_percentage_group_by_dropdown_title',
          })}
          locale={locale}
          placeholder={formatMessage({
            id: 'dbl_chart_dimension_percentage_group_by_placeholder',
          })}
          tooltip={null}
          options={dimensionOptions}
          listWidth={220}
          listHeight={180}
          searchable={false}
          onChange={handleGroupByChange}
        />
      </div>

      <div className="dbl-dimension-percentage-row">
        <span className="dbl-dimension-percentage-label">
          {formatMessage({ id: 'dbl_chart_dimension_percentage_label' })}
        </span>
        <Input
          className="dbl-dimension-percentage-input"
          value={labelDraft}
          maxLength={DIMENSION_PERCENTAGE_LABEL_MAX_LENGTH}
          placeholder={formatMessage({
            id: 'dbl_chart_dimension_percentage_label_placeholder',
          })}
          onChange={(value: string) => handleLabelChange(value)}
          onBlur={handleLabelBlur}
        />
      </div>
    </div>
  );
};

function metricDisplayOf(options: IMetricOption[], combined: string): string {
  const opt = options.find(o => o?.value === combined);
  return opt?.text || combined;
}

const DimensionPercentage: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const { value, setValue } =
    useSettingObjectState('dimension_percentage');

  const columnMetrics = useQueryColumnMetrics();
  const columnDimensions = useQueryColumnDimensions();

  // 兼容旧数据：可能是 undefined、缺字段；旧结构带根级 group_by 时容错丢弃
  const safeConfig: IDimensionPercentageConfig = useMemo(() => {
    const v: any = value;
    if (!v || typeof v !== 'object') return createDefaultDimensionPercentageConfig();
    return {
      metrics: Array.isArray(v.metrics) ? v.metrics : [],
    };
  }, [value]);

  const metricList = safeConfig.metrics || [];

  const metricOptions = useMemo((): IMetricOption[] => {
    return (columnMetrics as IColumnInfo[]).map(item => ({
      text: getLanguage(isEn, item?.column_display_en, item?.column_display),
      value: `${item?.table_code || 't0'}${DATALAB_SPLIT_STR}${item?.column_name}`,
    }));
  }, [columnMetrics, isEn]);

  const dimensionOptions = useMemo((): IDimensionOption[] => {
    return (columnDimensions as IColumnInfo[]).map(item => ({
      text: getLanguage(isEn, item?.column_display_en, item?.column_display),
      value: item?.column_name,
    }));
  }, [columnDimensions, isEn]);

  const handleAddMetric = useCallback(() => {
    // label 默认空：用户选完 metric 后由 MetricForm.handleMetricChange 自动填入 `%{metricName}`
    setValue({
      metrics: [...metricList, createDefaultDimensionPercentageMetric()],
    });
  }, [metricList, setValue]);

  const handleMetricChange = useCallback(
    (index: number) => (next: IDimensionPercentageMetricItem) => {
      setValue({
        metrics: metricList.map((m, i) => (i === index ? next : m)),
      });
    },
    [metricList, setValue],
  );

  const handleMetricDelete = useCallback(
    (index: number) => () => {
      setValue({
        metrics: metricList.filter((_, i) => i !== index),
      });
    },
    [metricList, setValue],
  );

  // 为每张卡片预先算好「其他卡片已选 metric 列表」，
  // 让 MetricForm 在 handleMetricChange 内部基于 otherMetrics 实时算 sameMetricCount
  // ——这样空卡片选 metric 的瞬间也能拿到正确的同 metric 数量
  // 例：metricList=[revenue, ''] →
  //   card1.otherMetrics=[{t0,revenue}]   （card1 自己不算；card2.metric 是空，过滤掉）
  //   card2.otherMetrics=[{t0,revenue}]
  // 当 card2 选 revenue 时，computeSameMetricCount(t0, revenue) = 1 → label 加 _2
  const otherMetricsList = useMemo<Array<Array<{ table_code: string; metric: string }>>>(
    () =>
      metricList.map((_, i) =>
        metricList
          .filter((__, j) => i !== j && metricList[j]?.metric)
          .map(m => ({
            table_code: m?.table_code || 't0',
            metric: m?.metric || '',
          })),
      ),
    [metricList],
  );

  const isEmpty = metricList.length === 0;

  return (
    <ConfigCollapseCard
      title={formatMessage({
        id: 'dbl_chart_dimension_percentage_title',
      })}
      info={{
        tipText: formatMessage({
          id: 'dbl_chart_dimension_percentage_title_info',
        }),
      }}
      className="dbl-dimension-percentage"
      defaultActiveIds={[]}
    >
      <div
        className="dbl-dimension-percentage-content"
        data-empty={isEmpty || undefined}
      >
        {metricList.map((item, index) => (
          <MetricForm
            key={`dp-metric-${index}`}
            count={index + 1}
            otherMetrics={otherMetricsList[index] || []}
            data={item}
            metricOptions={metricOptions}
            dimensionOptions={dimensionOptions}
            onChange={handleMetricChange(index)}
            onDelete={handleMetricDelete(index)}
          />
        ))}

        <PlusButton
          className="dbl-dimension-percentage-add"
          title={formatMessage({
            id: 'dbl_chart_dimension_percentage_add_metric',
          })}
          onClick={handleAddMetric}
        />
      </div>
    </ConfigCollapseCard>
  );
};

/**
 * 重新导出供外部（reducer、handler）复用：清洗整条配置
 *
 * 行为（更新于 2026-06-30）：
 * - 必须有 `metric`（且非后端禁止前缀 `%` / `dim_pct_`） + 非空 `group_by[]`
 * - `label` 允许为空字符串：空时尝试用 `defaultLabelForMetric(metricDisplay)` 重置
 *   - 调用方传入了 `metricOptions` 且能查到 metric 展示名 → 重置 label 后保留
 *   - 无法重置（未传 metricOptions / 查不到展示名）→ 剔除整条 metric
 * - 重置 / trim 后 label 长度 > `DIMENSION_PERCENTAGE_LABEL_MAX_LENGTH` → 剔除整条 metric
 * - 整体清洗后为空 → 返回 `undefined`（不发送 dimension_percentage 字段）
 */
export function sanitizeDimensionPercentage(
  cfg: IDimensionPercentageConfig | undefined,
  metricOptions?: IMetricOption[],
): IDimensionPercentageConfig | undefined {
  if (!cfg) return undefined;
  const cleaned: IDimensionPercentageMetricItem[] = [];
  for (const m of Array.isArray(cfg.metrics) ? cfg.metrics : []) {
    if (!m?.metric) continue;
    if (hasForbiddenPrefix(m.metric)) continue;
    if (!Array.isArray(m.group_by) || m.group_by.length === 0) continue;
    if (!m.group_by.every(isDimensionPercentageGroupByValid)) continue;

    const trimmed = (m.label || '').trim();
    if (trimmed) {
      if (trimmed.length > DIMENSION_PERCENTAGE_LABEL_MAX_LENGTH) continue;
      cleaned.push({ ...m, label: trimmed });
      continue;
    }

    // label 空 → 尝试用默认重置（UI 失焦时也会回退；这里是数据层兜底）
    const combined = `${m.table_code || 't0'}${DATALAB_SPLIT_STR}${m.metric}`;
    const display = metricOptions ? metricDisplayOf(metricOptions, combined) : '';
    if (!display) continue;
    cleaned.push({ ...m, label: defaultLabelForMetric(display) });
  }
  if (cleaned.length === 0) return undefined;
  return { metrics: cleaned };
}

export default React.memo(DimensionPercentage);
